import Services from './index';
import { CARRIER_STATS_URL, STATS_SERVLET_URL, STATS_URL, CAMPAIGN_STATS_URL } from 'consts/api';
import { momentInstance } from 'helpers/date-time';
import { FORMAT_STATS } from 'consts/data';
import { timezoneHelpers } from 'providers/TimezoneProvider';
import DATE_FORMATS, { DEFAULT_DATETIME_REQUEST_ONLY_HOURS_FORMAT } from 'consts/dateFormats';

class StatsServices {
  getCampaignClickStats(cpId = '') {
    return Services.post(`${STATS_SERVLET_URL}?action=getCampaignClickStats&cpId=${cpId}`);
  }

  getCampaignActiveCallStats(body = { cpId: '', from: '', to: '' }) {
    const { cpId, from, to } = body;
    return Services.post(
      `${STATS_SERVLET_URL}?action=getCampaignActiveCallStats&cpId=${cpId}&from=${from}&to=${to}`
    );
  }

  getStats(body) {
    return Services.post(STATS_SERVLET_URL, body);
  }

  getStatsByRange(range) {
    return Services.get(`${STATS_URL}?range=${range}`);
  }

  getStatsByTime(body = { from: '', to: '' }, configs) {
    return Services.post(`${STATS_URL}`, body, configs);
  }

  getMessageStats({ from, to }) {
    const dateRanges = timezoneHelpers.convertDateRangeByTimezone({
      from: from,
      to: to,
      format: DATE_FORMATS.DATE_ONLY,
    });
    return Services.get(`${CARRIER_STATS_URL}?from=${dateRanges.from}&to=${dateRanges.to}`);
  }

  getHistHighlight({ from, to }) {
    return Services.get(
      `${STATS_SERVLET_URL}?action=getHistHighlight&startDate=${from}&endDate=${to}`
    );
  }

  getStatsCp({ from, to, cpId }) {
    const fromDate = momentInstance(from).format(FORMAT_STATS);
    const toDate = momentInstance(to).format(FORMAT_STATS);

    return Services.post(`${STATS_SERVLET_URL}/getStats`, {
      from: fromDate,
      to: toDate,
      cpId,
    });
  }

  // https://textback.atlassian.net/browse/TA-13579 - startDate/endDate/lastSendDateFrom/lastSendDateTo the format of these fields should be like yyyy-MM-dd-HH
  getCampaignStats(
    body = {
      isPreview: true,
      domainGroupId: '658',
      messageType: 'SMS',
      fromPhone: 968,
      title: 'Campaign title',
      startDate: '2023/10/30',
      endDate: '2023/10/30',
      lastSendDateFrom: '2023/10/29',
      lastSendDateTo: '2023/10/30',
    }
  ) {
    if (body.isPreview) {
      return Services.post(`${CAMPAIGN_STATS_URL}`, body);
    }

    return Services.post(`${CAMPAIGN_STATS_URL}`, body, {
      responseType: 'blob',
    });
  }

  getDomainStats({ domain, from = '', to = '' }) {
    const dateRanges = timezoneHelpers.convertDateRangeByTimezone({
      from: from,
      to: to,
      format: DEFAULT_DATETIME_REQUEST_ONLY_HOURS_FORMAT,
    });

    return Services.get(
      `/tb/api/v2/authenticated/stats/detail-by-domains?domains=${encodeURIComponent(
        domain
      )}&startDateHour=${dateRanges.from}&endDateHour=${dateRanges.to}`
    );
  }

  getGroupDomainStats({ groupId, from = '', to = '' }) {
    const dateRanges = timezoneHelpers.convertDateRangeByTimezone({
      from: from,
      to: to,
      format: DEFAULT_DATETIME_REQUEST_ONLY_HOURS_FORMAT,
    });

    return Services.get(
      `/tb/api/v2/authenticated/stats/summary-by-domain-group-id?groupId=${groupId}&startDateHour=${dateRanges.from}&endDateHour=${dateRanges.to}`
    );
  }

  getPhoneStats({ phone, from = '', to = '' }) {
    const dateRanges = timezoneHelpers.convertDateRangeByTimezone({
      from: from,
      to: to,
      format: DEFAULT_DATETIME_REQUEST_ONLY_HOURS_FORMAT,
    });

    return Services.get(
      `/tb/api/v2/authenticated/stats/detail-by-phones?phones=${encodeURIComponent(
        phone
      )}&startDateHour=${dateRanges.from}&endDateHour=${dateRanges.to}`
    );
  }

  getGroupDidStats({ groupId, from = '', to = '' }) {
    const dateRanges = timezoneHelpers.convertDateRangeByTimezone({
      from: from,
      to: to,
      format: DEFAULT_DATETIME_REQUEST_ONLY_HOURS_FORMAT,
    });

    return Services.get(
      `/tb/api/v2/authenticated/stats/summary-by-phone-group-id?groupId=${groupId}&startDateHour=${dateRanges.from}&endDateHour=${dateRanges.to}`
    );
  }

  getErrorStats(
    body = {
      fromTime: '',
      toTime: '',
      viewType: '',
      granularity: '',
      username: null,
      cpId: null,
      vendorId: null,
    },
    configs
  ) {
    return Services.post(`/tb/api/v2/authenticated/stats/error`, body, configs);
  }

  getCampaignMediaInGroup(
    body = {
      campaignId: '',
      offset: 0,
      limit: 100,
    }
  ) {
    return Services.post('/tb/api/v2/authenticated/stats/campaignMedia', body);
  }

  getCampaignReport(
    body = {
      startDateFrom: '2024-04-02T00:00',
      startDateTo: '2024-04-03T23:59',
      statuses: ['running', 'paused', 'scheduled', 'completed'],
      country: 'US',
    }
  ) {
    return Services.post('/tb/api/v2/authenticated/stats/campaign/report', body);
  }

  getForwardCallStats(
    body = {
      campaignId: '',
      forwardNumber: '+1123254368',
      pageNo: 1,
      pageSize: 50,
    },
    configs
  ) {
    return Services.post(`${STATS_SERVLET_URL}/getForwardCallStats`, body, configs);
  }

  exportForwardCallStats(
    body = {
      campaignId: 14084,
      forwardNumber: '1123254368',
    },
    configs
  ) {
    return Services.post(`${STATS_SERVLET_URL}/exportForwardCallStats`, body, configs);
  }

  getCategorizedStats(
    body = {
      from: '2024-11-03-15',
      to: '2025-01-01-18',
      timezone: 'Etc/GMT',
    },
    configs
  ) {
    return Services.post('/tb/api/v2/authenticated/user/me/categorizedStats', body, configs);
  }

  getErrorStatsMessage(
    body = {
      fromTime: '',
      toTime: '',
      viewType: '',
      granularity: '',
      username: null,
      cpId: null,
      vendorId: null,
    },
    configs
  ) {
    return Services.post(`/tb/api/v2/authenticated/stats/error-message`, body, configs);
  }
}

export default new StatsServices();
